/**
 * Resumen: la primera pantalla del panel, acotada a la tienda seleccionada.
 *
 * Que reemplaza
 * -------------
 * `src/gui/views/home_view.py` (259 LOC), que era la pantalla inicial del GUI
 * de escritorio. Aquella hacia dos cosas:
 *
 * 1. Cuatro tarjetas que EJECUTABAN workflows con un click (`daily_sync`,
 *    `full_publication`, `check_services`, `sync_only`).
 * 2. Un panel de estado con cuatro numeros, calculados abriendo una sesion de
 *    SQLAlchemy contra Postgres desde un hilo del proceso de la UI. Dos de esos
 *    numeros eran placeholders que nunca se llenaban ("Ultima sincronizacion:
 *    --") y el conteo de "pendientes de publicar" tenia un bug: cuando la
 *    tienda destino estaba vacia, `Product.ml_id.notin_(...)` se reemplazaba
 *    por `True` y contaba TODO el catalogo como pendiente.
 *
 * Lo que cambia aca, y por que
 * ----------------------------
 * * **Los numeros salen de la API, no de una conexion directa a la base.** Un
 *   navegador no puede --ni debe-- abrir Postgres. Y de paso la tenencia deja
 *   de ser un supuesto: cada request nombra la tienda y el backend responde 403
 *   si el usuario no la tiene concedida.
 * * **No hay botones que ejecuten workflows.** Dos de los cuatro escriben en
 *   cuentas reales de MercadoLibre y ninguno tiene hoy un endpoint que lo
 *   dispare (la task de sincronizacion ni siquiera existe: ver `SyncPanel`).
 *   Publicar mal en la cuenta real no se corrige, se da de baja a mano; encolar
 *   una publicacion tiene su propia pantalla, con seleccion de productos y de
 *   tienda destino.
 * * **Ningun contador inventado.** "Pendientes de publicar" no esta porque
 *   requiere comparar las dos tiendas y ningun endpoint expone esa comparacion
 *   (la produce `compare_stores_from_db.py`, en un Excel). Repetir aca la
 *   consulta del GUI habria significado repetir tambien su bug.
 *
 * La regla de esta pantalla: un cero se explica o no se muestra
 * ------------------------------------------------------------
 * Varias tablas del sistema estan vacias de verdad (`campaigns`,
 * `pricing_history`, `competitor_prices`, y para muchas tiendas tambien
 * `publication_jobs`). Un tablero con tarjetas en cero por todos lados se lee
 * como roto aunque cada numero sea correcto, asi que:
 *
 * * los ceros que son una MEDICION se muestran (0 productos sin stock, 0 sin
 *   `ml_id`: ahi el cero es la respuesta, y ademas es la buena);
 * * los ceros que son AUSENCIA DE USO no ocupan una tarjeta: van a una lista al
 *   pie, cada uno con el motivo verificado en el codigo por el que esta vacio
 *   (`QuietModules`);
 * * los estados sin filas no se dibujan como tarjeta, se nombran en una linea
 *   (`CatalogPanel`);
 * * y lo que el backend NO puede contestar se dice explicitamente en vez de
 *   rellenarse: si la ultima sincronizacion fallo, no se sabe, porque no hay
 *   tabla de corridas (`SyncPanel` muestra la nota que manda el propio
 *   backend).
 *
 * De donde sale cada dato
 * -----------------------
 *   GET /api/v1/monitoring/summary?store_id=  -> catalogo y publicacion
 *   GET /api/v1/monitoring/sync?store_id=     -> antiguedad y lock de la sync
 *   GET /api/v1/invoicing/status/{store_id}   -> facturas de hoy y de 7 dias
 *   GET /api/v1/stores/{store_id}/stats       -> los modulos sin datos
 *
 * Son cuatro requests en paralelo, cada una con su propio estado: si la de
 * facturacion falla, el resto de la pantalla se dibuja igual. Un solo
 * `try/catch` alrededor de todo convertiria una caida parcial en una pantalla
 * en blanco.
 *
 * No hay polling. Los datos son de fondo (un catalogo que cambia con una sync
 * diaria), y un refresco automatico cada N segundos serian cuatro requests por
 * pestania abierta todo el dia contra Cloud Run para ver el mismo numero. El
 * boton "Actualizar" recarga los cuatro paneles a la vez.
 */

import { getStoreStats } from "../api/endpoints";
import type { Store, StoreStats } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { Async } from "../components/AsyncBoundary";
import { useAsync } from "../components/useAsync";
import {
  getInvoicingStatus,
  getStoreSummary,
  getStoreSyncSnapshot,
  type InvoicingStatus,
  type StoreSummary,
  type StoreSyncSnapshot,
} from "./home/api";
import { CatalogPanel } from "./home/CatalogPanel";
import { formatDateTime } from "./home/format";
import { InvoicingPanel } from "./home/InvoicingPanel";
import { PublicationPanel } from "./home/PublicationPanel";
import { QuickLinks } from "./home/QuickLinks";
import { QuietModules } from "./home/QuietModules";
import { StoreHealth } from "./home/StoreHealth";
import { SyncPanel } from "./home/SyncPanel";
import "./home/home.css";

export function StoreOverviewScreen({ store }: { readonly store: Store }) {
  const { api } = useAuth();

  const summary = useAsync<StoreSummary>(
    (signal) => getStoreSummary(api, store.id, { signal }),
    [api, store.id],
  );
  const sync = useAsync<StoreSyncSnapshot>(
    (signal) => getStoreSyncSnapshot(api, store.id, { signal }),
    [api, store.id],
  );
  const invoicing = useAsync<InvoicingStatus>(
    (signal) => getInvoicingStatus(api, store.id, { signal }),
    [api, store.id],
  );
  const stats = useAsync<StoreStats>(
    (signal) => getStoreStats(api, store.id, { signal }),
    [api, store.id],
  );

  function reloadAll(): void {
    summary.reload();
    sync.reload();
    invoicing.reload();
    stats.reload();
  }

  // `has_ml_tokens` solo lo sabe el resumen. Mientras no llegue se pasa `null`,
  // que el badge dibuja como "consultando": mostrar "sin vincular" durante la
  // carga mandaria a rehacer un OAuth que esta perfecto.
  const hasMlTokens = summary.state.kind === "ready" ? summary.state.data.has_ml_tokens : null;

  return (
    <section className="screen">
      <header className="screen__header">
        <h2>{store.name}</h2>
        {store.description ? <p className="screen__lead">{store.description}</p> : null}
      </header>

      <StoreHealth store={store} hasMlTokens={hasMlTokens} />

      <div className="home__toolbar">
        <button type="button" onClick={reloadAll}>
          Actualizar
        </button>
        {sync.state.kind === "ready" ? (
          <span className="home__generated">
            Datos leidos por el servidor el {formatDateTime(sync.state.data.generated_at)}
          </span>
        ) : null}
      </div>

      <div className="home__panels">
        <Async
          state={summary.state}
          onRetry={summary.reload}
          loadingLabel="Contando el catalogo..."
        >
          {(data) => <CatalogPanel counts={data.products} />}
        </Async>

        <Async
          state={sync.state}
          onRetry={sync.reload}
          loadingLabel="Consultando la sincronizacion..."
        >
          {(data) => <SyncPanel snapshot={data} />}
        </Async>

        <Async
          state={invoicing.state}
          onRetry={invoicing.reload}
          loadingLabel="Contando facturas..."
        >
          {(data) => <InvoicingPanel status={data} />}
        </Async>

        <Async
          state={summary.state}
          onRetry={summary.reload}
          loadingLabel="Contando publicaciones..."
        >
          {(data) => <PublicationPanel summary={data} />}
        </Async>

        <Async state={stats.state} onRetry={stats.reload} loadingLabel="Contando modulos...">
          {(data) => <QuietModules stats={data} />}
        </Async>
      </div>

      <h3>Ir a</h3>
      <QuickLinks />
    </section>
  );
}
